import * as XLSX from 'xlsx';
import { parseSubscribers, formatSubscribers } from './subscriberParser';
import { getEffectiveTier } from './effectiveTier';
import { formatDuration, getBrandDurationSeconds, getTotalDurationSeconds, isTimeBasedMedia } from './editorialOccupancy';

const toNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const EXCEL_DATE_TIMEZONE = 'America/Sao_Paulo';

const toExcelCivilDate = (value) => {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: EXCEL_DATE_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(date);

    const getPart = (type) => Number(parts.find(part => part.type === type)?.value);
    const year = getPart('year');
    const month = getPart('month');
    const day = getPart('day');

    if (year && month && day) {
      return new Date(year, month - 1, day, 12, 0, 0, 0);
    }
  } catch {
    // Fallback below keeps export working in browsers without full Intl timezone data.
  }

  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 12, 0, 0, 0);
};

const getEditorialMetrics = (article) => {
  const occupancy = article.editorialOccupancy || article.onlineCentimetragem || {};
  const totalAreaCm2 = toNumber(occupancy.editorialAreaTotalCm2) || toNumber(occupancy.valueCm2);
  const brandAreaCm2 = toNumber(occupancy.editorialAreaBrandRelevantCm2) || toNumber(occupancy.valueCm2) || totalAreaCm2;
  const rawParticipation = toNumber(occupancy.brandParticipationPercent);
  const participationPercent = rawParticipation > 0
    ? (rawParticipation > 1 ? rawParticipation : rawParticipation * 100)
    : (totalAreaCm2 > 0 ? (brandAreaCm2 / totalAreaCm2) * 100 : 0);

  return {
    totalAreaCm2,
    brandAreaCm2,
    participationPercent: Math.round(participationPercent * 100) / 100
  };
};

/**
 * Identifica se o artigo possui evidência persistida de participação concorrente.
 * A análise editorial é a fonte mais específica; os campos legados ficam como fallback.
 */
const hasCompetitorFocus = (article) => {
  if (article.entityFocusType) return ['competitor', 'both'].includes(article.entityFocusType);

  const occupancy = article.editorialOccupancy || article.onlineCentimetragem || {};

  if (occupancy.contentProtagonistType === 'competitor') return true;
  if (occupancy.contentProtagonistType === 'brand') return false;

  if (Array.isArray(occupancy.entityBreakdown)) {
    const mainEntity = [...occupancy.entityBreakdown]
      .filter(entity => entity?.type === 'brand' || entity?.type === 'competitor')
      .sort((a, b) => toNumber(b.areaCm2) - toNumber(a.areaCm2))[0];
    if (mainEntity) return mainEntity.type === 'competitor';
  }

  return toNumber(article.exposureAnalysis?.keywordCount) === 0 &&
    Array.isArray(article.exposureAnalysis?.competitorsMentioned) &&
    article.exposureAnalysis.competitorsMentioned.length > 0;
};

const getEntityLabelsForTag = (tag) => {
  if (tag?.classificationType === 'brand') return ['Marca'];
  if (tag?.classificationType === 'competitor') return ['Concorrente'];
  if (tag?.briefingEntityType === 'both') return ['Marca', 'Concorrente'];
  if (tag?.briefingEntityType === 'brand') return ['Marca'];
  if (tag?.briefingEntityType === 'competitor') return ['Concorrente'];
  return [];
};

const getArticleEntityLabels = (article) => {
  const labelsFromTags = (article.tags || []).flatMap(getEntityLabelsForTag);
  const labelsFromApi = (article.tagEntityTypes || []).flatMap(type => (
    type === 'brand' ? ['Marca'] : type === 'competitor' ? ['Concorrente'] : []
  ));
  const labels = [...new Set([...labelsFromTags, ...labelsFromApi])];

  if (labels.length > 0) return labels;
  return hasCompetitorFocus(article) ? ['Concorrente'] : ['Marca'];
};

/**
 * Exporta artigos para arquivo Excel
 * @param {Array} articles - Array de artigos para exportar
 * @param {string} filename - Nome do arquivo (sem extensão)
 * @param {string} tagMode - Modo de exibição de tags:
 *   'per_line' (padrão): uma linha por tag — artigos com múltiplas tags aparecem repetidos
 *   'comma': uma linha por artigo — tags separadas por vírgula na mesma célula
 */
export const exportArticlesToExcel = (articles, filename = 'artigos', tagMode = 'per_line') => {
  if (!articles || articles.length === 0) {
    throw new Error('Nenhum artigo para exportar');
  }

  const data = [];

  articles.forEach((article, index) => {
    const dailyAudience = article.audience || article.subscribersNumber || 0;
    const editorialMetrics = getEditorialMetrics(article);
    const effectiveTier = getEffectiveTier(article);

    let monthlyAudience = 0;
    if (article.audienceMonthly > 0) {
      monthlyAudience = article.audienceMonthly;
    } else {
      monthlyAudience = Math.round(dailyAudience * (365 / 12));
    }

    const articleEntityLabels = getArticleEntityLabels(article);
    const focoGlobal = articleEntityLabels.join(', ');

    const createBaseRow = (tagValue, focusValue = focoGlobal) => ({
      'Article ID': article._id || 'N/A',
      'Foco Global': focusValue,
      '#': index + 1,
      'Título': article.title || 'Sem título',
      'Plataforma': article.mediaType || 'N/A',
      'Autor': article.author || 'Desconhecido',
      'Site': article.siteName || 'N/A',
      'Editoria': article.section || 'N/A',
      'URL': article.siteUrl || article.url || 'N/A',
      'LinkM': article._id ? `${window.location.origin}/article/${article._id}` : 'N/A',
      'Data de Criação': toExcelCivilDate(article.createdAt),
      'Data de Publicação': toExcelCivilDate(article.date),
      'Sentimento': article.sentiment === 'positive' ? 'Positivo' :
        article.sentiment === 'negative' ? 'Negativo' : 'Neutro',
      'Exposição': article.exposure === 'dedicated' ? 'Dedicada' :
        article.exposure === 'shared' ? 'Compartilhada' :
        article.exposure === 'mention' ? 'Citação' :
        article.exposure === 'none' ? 'Nenhuma' : 'N/A',
      'Favorito': article.isFavorite ? 'Sim' : 'Não',
      'Lido': article.isRead ? 'Sim' : 'Não',

      'Abrangência': article.domainInfo?.abrangencia || article.abrangencia || 'N/A',
      'Categorias': (article.domainInfo?.categories || article.categories || []).join(', ') || 'N/A',
      'UF': article.domainInfo?.uf || article.uf || 'N/A',
      'Estado': article.domainInfo?.estado || article.estado || 'N/A',
      'Cidade': article.domainInfo?.cidade || article.domainInfo?.city || article.cidade || article.city || 'N/A',
      'País': article.domainInfo?.pais || article.pais || 'Brasil',
      'Tipo': article.domainInfo?.tipo || article.domainInfo?.category || 'N/A',
      'Tier': effectiveTier ? `Tier ${effectiveTier}` : 'N/A',

      'Audiência Mensal': monthlyAudience || 0,
      'Audiência Diária': dailyAudience || 0,
      'Page Views': article.mediaType === 'Online' ? Math.round(monthlyAudience * 3) : (article.viewsNumber || 0),
      'Page Views Diário': article.mediaType === 'Online' ? Math.round(dailyAudience * 3) : 0,
      'Valoração': article.valuation || 0,
      'Impacto': article.impact || 0,
      'Centimetragem (cm2)': editorialMetrics.brandAreaCm2,
      'Área Editorial (cm²)': editorialMetrics.brandAreaCm2,
      'Área Editorial Total (cm²)': editorialMetrics.totalAreaCm2,
      'Participação Editorial (%)': editorialMetrics.participationPercent,
      'Duração Total (segundos)': isTimeBasedMedia(article) ? getTotalDurationSeconds(article) : 0,
      'Duração Total': isTimeBasedMedia(article) ? formatDuration(getTotalDurationSeconds(article)) : '',
      'Minutagem da Marca (segundos)': isTimeBasedMedia(article) ? getBrandDurationSeconds(article) : 0,
      'Minutagem da Marca': isTimeBasedMedia(article) ? formatDuration(getBrandDurationSeconds(article)) : '',

      'Visualizações (Social)': article.viewsNumber || 0,
      'Inscritos': article.subscribersNumber || 0,
      'Likes': article.likesNumber || 0,
      'Comentários': article.commentsNumber || 0,
      'Compartilhamentos': article.sharesNumber || 0,

      // Nome da coluna muda conforme o modo
      [tagMode === 'comma' ? 'Tags' : 'Tag']: tagValue
    });

    if (tagMode === 'comma') {
      // MODO VÍRGULA: uma linha por artigo, tags concatenadas
      const tagsStr = article.tags && article.tags.length > 0
        ? article.tags.map(tag => typeof tag === 'object' ? (tag.name || 'Tag sem nome') : tag).join(', ')
        : 'Sem tags';
      data.push(createBaseRow(tagsStr, articleEntityLabels.join(', ')));
    } else {
      // MODO POR LINHA (padrão): uma linha por tag
      if (article.tags && article.tags.length > 0) {
        article.tags.forEach(tag => {
          const tagName = typeof tag === 'object' ? (tag.name || 'Tag sem nome') : tag;
          const tagEntityLabels = typeof tag === 'object' ? getEntityLabelsForTag(tag) : [];
          const tagFocus = tagEntityLabels.length === 1 ? tagEntityLabels[0] : 'Neutra';
          data.push(createBaseRow(tagName, tagFocus));
        });
      } else {
        data.push(createBaseRow('Sem tags'));
      }
    }
  });

  // Criar workbook e worksheet
  const ws = XLSX.utils.json_to_sheet(data);

  // Aplicar formato de data (tipo data real do Excel)
  const range = XLSX.utils.decode_range(ws['!ref']);
  for (let R = range.s.r + 1; R <= range.e.r; ++R) {
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cell_address = { c: C, r: R };
      const cell_ref = XLSX.utils.encode_cell(cell_address);
      const cell = ws[cell_ref];

      if (!cell || cell.t !== 'd') continue;

      // Pegar o header desta coluna
      const header_address = { c: C, r: range.s.r };
      const header_ref = XLSX.utils.encode_cell(header_address);
      const header = ws[header_ref]?.v;

      if (header === 'Data de Criação' || header === 'Data de Publicação') {
        cell.z = 'dd/mm/yyyy'; // Formato de data brasileiro no Excel
      }
    }
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Artigos');

  // Ajustar largura das colunas
  const columnWidths = [
    { wch: 25 },  // Article ID
    { wch: 30 },  // Foco Global
    { wch: 5 },   // #
    { wch: 50 },  // Título
    { wch: 15 },  // Plataforma
    { wch: 20 },  // Autor
    { wch: 20 },  // Site
    { wch: 20 },  // Editoria
    { wch: 50 },  // URL
    { wch: 60 },  // LinkM
    { wch: 20 },  // Data Criação
    { wch: 20 },  // Data Publicação
    { wch: 12 },  // Sentimento
    { wch: 15 },  // Exposição
    { wch: 10 },  // Favorito
    { wch: 8 },   // Lido
    { wch: 15 },  // Abrangência
    { wch: 6 },   // UF
    { wch: 15 },  // Estado
    { wch: 20 },  // Cidade
    { wch: 12 },  // País
    { wch: 20 },  // Categorias
    { wch: 15 },  // Tipo
    { wch: 10 },  // Tier
    { wch: 18 },  // Aud Mensal
    { wch: 18 },  // Aud Diária
    { wch: 18 },  // Page Views
    { wch: 18 },  // Page Views Diário
    { wch: 15 },  // Valoração
    { wch: 15 },  // Impacto
    { wch: 20 },  // Centimetragem
    { wch: 20 },  // Área Editorial
    { wch: 24 },  // Área Editorial Total
    { wch: 24 },  // Participação Editorial
    { wch: 15 },  // Visualizações (Social)
    { wch: 15 },  // Inscritos
    { wch: 12 },  // Likes
    { wch: 15 },  // Comentários
    { wch: 18 },  // Compartilhamentos
    { wch: 30 }   // Tag / Tags
  ];
  ws['!cols'] = columnWidths;

  // Gerar arquivo
  const timestamp = new Date().toISOString().split('T')[0];
  const fileName = `${filename}_${timestamp}.xlsx`;
  XLSX.writeFile(wb, fileName);

  return fileName;
};

/**
 * Exporta apenas artigos selecionados
 * @param {Array} articles - Array com todos os artigos
 * @param {Array|Set} selectedIds - Array ou Set com IDs dos artigos selecionados
 * @param {string} filename - Nome do arquivo
 * @param {string} tagMode - Modo de exibição de tags ('per_line' ou 'comma')
 */
export const exportSelectedArticles = (articles, selectedIds, filename = 'artigos_selecionados', tagMode = 'per_line') => {
  const isInSelection = Array.isArray(selectedIds)
    ? (id) => selectedIds.includes(id)
    : (id) => selectedIds.has(id);

  const selectedArticles = articles.filter(article => isInSelection(article._id));
  return exportArticlesToExcel(selectedArticles, filename, tagMode);
};
